// Raise a Ticket queue + flusher (card #2617).
//
// Every card is written to the pending_cards Dexie store FIRST, then sent
// to the server -- straight away when online (network-first, Article #142),
// otherwise from the regular sync cycle (field_sync_controller.js calls
// syncCards alongside syncLogs) or when the cards page sees the device come
// back online. A card raised with no signal is never lost.
//
// Same shape as field_log_sync.js: observedFetch so the request feeds the
// connectivity evidence stream, timeoutFor for the abort budget, the CSRF
// header for completeness (the endpoint skips CSRF, like LogsController, so a
// cached shell's stale token can't strand a card).
//
// Outcomes per row (classifySyncResponse, field_sync_shared.js):
//   2xx                     -> "sent" (photo blob dropped, text kept)
//   401 / 419 / sign-in bounce / CSRF-shaped 422 -> "reauth-required", retried
//   422 naming BOTH "blank body" and "bad ticket reference" while the local
//   row has both -> the server saw no parsed fields (a cut-off multipart
//   body), so it is a transport failure: left "pending", retried. Not
//   forever (card #4115): after CUT_OFF_PHOTO_LIMIT of these for a row with a
//   photo the photo is dropped and the text is sent on its own; after
//   CUT_OFF_TEXT_LIMIT for a text-only row it is marked "failed" and shown.
//   any other 4xx           -> "failed", never retried, shown on the page
//   5xx / network / timeout -> left "pending", retried next cycle
//
// Photos are held as plain bytes (card #4115, the ADR-0140 lesson): on iOS a
// Blob read back from IndexedDB can come back empty, which reached the server
// as an empty multipart request. A ticket holds several of them (card #4190) as
// `photos`: [{ bytes: ArrayBuffer, type, name }], and a fresh File is built
// from each at send time, sent as photos[]. `photoTotal` is how many the
// tasker chose, `photosDropped` how many did not go. None of these are
// indexed, so the Dexie schema is unchanged.
//
// Older rows still work. Before #4190 a row held one photo as photoBytes /
// photoType / photoName, and before #4115 as a Blob in `photo`. The first send
// reads either once and moves it into `photos`, or drops it if it will not
// read. (An older cached app posting a single `photo` field is accepted by the
// server the same way.)
//
// The server dedupes on clientId, so a row that was actually filed but whose
// 2xx never made it back is safely re-sent.

import db from "./field_db"
import { observedFetch, currentLevel } from "./field_network_health"
import { timeoutFor } from "./field_timeout_policy"
import { classifySyncResponse } from "./field_sync_shared"
import { logEvent, logWarn, logError, getPlatformSnapshot } from "./field_logger"

const CARD_URL = "/api/v1/field/cards"
const RETRYABLE_STATUSES = ["pending", "reauth-required"]

// Requests::CreateFromField adds both of these when it received no body and no
// client_id. Seeing both for a row whose body and clientId are valid locally
// means the request arrived cut off, not that the ticket is wrong.
const CUT_OFF_MESSAGES = ["Tell us what's going wrong", "Ticket reference is missing or invalid"]
const CLIENT_ID_FORMAT = /^[0-9a-f-]{8,64}$/i

export function isCutOffRejection(row, errors) {
  const text = (errors || []).join(", ")
  const serverSawNothing = CUT_OFF_MESSAGES.every(message => text.includes(message))
  const rowIsValid = String(row?.body || "").trim() !== "" && CLIENT_ID_FORMAT.test(String(row?.clientId || ""))
  return serverSawNothing && rowIsValid
}

// Cut-off 422s tolerated for a row WITH a photo before the photo is dropped
// and the text is sent alone, and for a text-only row before it is shown as
// not sent. Genuine network trouble (timeouts, 5xx) is not counted.
export const CUT_OFF_PHOTO_LIMIT = 3
export const CUT_OFF_TEXT_LIMIT = 10

// Why a photo did not go. Stored on the row as photoDropped.
export const PHOTO_DROPPED = { UNREADABLE: "unreadable", CUT_OFF: "cut_off" }

// What the Tasker is told when a ticket goes (or will go) without some or all
// of its photos. Rows from before #4190 carry no counts and mean one photo.
export function photoDroppedNote(row, { sent }) {
  const dropped = Math.max(row.photosDropped || 1, 1)
  const total = Math.max(row.photoTotal || dropped, dropped)

  if (total === 1) {
    return sent
      ? "Sent without the photo. The photo could not be read on this phone."
      : "The photo could not be read on this phone, so this ticket will go without it."
  }

  if (dropped === total) {
    return sent
      ? "Sent without the photos. They could not be read on this phone."
      : "The photos could not be read on this phone, so this ticket will go without them."
  }

  const which = `${dropped} of ${total} photos`
  return sent
    ? `Sent without ${which}. ${dropped === 1 ? "It" : "They"} could not be read on this phone.`
    : `${which} could not be read on this phone, so this ticket will go without ${dropped === 1 ? "it" : "them"}.`
}

const SENT_TTL_MS = 30 * 24 * 60 * 60 * 1000

// Fired on window whenever a row is added or changes state, so the cards
// page can re-render its queued list without polling.
export const CARDS_CHANGED_EVENT = "field:cards-changed"

let _inFlight = null

export async function queueCard({ hubId = null, categoryTags = [], body, context = {}, photos = [], photo = null }) {
  const clientId = _uuid()
  const chosen = [...(photos || []), ...(photo ? [photo] : [])]
  const stored = await _photoFields(chosen)

  const record = {
    clientId,
    hubId,
    categoryTags,
    body,
    context,
    ...stored.fields,
    status: "pending",
    queuedAt: new Date().toISOString()
  }
  record.id = await db.pending_cards.add(record)

  if (stored.unreadable) _logPhotoUnreadable(record, "queue")

  logEvent("cards", "Card queued", {
    clientId,
    categoryTags,
    photoCount: (record.photos || []).length
  }).catch(() => {})
  _announceChange()

  return record
}

// Sends every pending card, oldest first. Safe to call from several places
// at once: concurrent callers share the one in-flight pass rather than
// posting the same row twice.
export function syncCards(csrfToken) {
  if (_inFlight) return _inFlight

  _inFlight = _flushPending(csrfToken).finally(() => { _inFlight = null })
  return _inFlight
}

// Cards still on this phone that haven't reached the server, newest first.
// Sent ones drop out here -- they show under "My tickets" from the server.
export async function cardsForDisplay() {
  const rows = await db.pending_cards.orderBy("queuedAt").reverse().toArray()
  // A ticket sent without its photo stays listed (until dismissed) so the
  // Tasker is told, even when it went in the background.
  return rows.filter(row => row.status !== "sent" || row.photoDropped)
}

export async function removeCard(id) {
  await db.pending_cards.delete(id)
  _announceChange()
}

// -- private --

async function _flushPending(csrfToken) {
  if (typeof localStorage !== "undefined" && localStorage.getItem("field:online") === "false") {
    return { sent: 0, failed: 0 }
  }

  await _purgeOldSent()

  const pending = await db.pending_cards
    .where("status").anyOf(RETRYABLE_STATUSES)
    .sortBy("queuedAt")

  let sent = 0
  let failed = 0

  for (const row of pending) {
    const outcome = await _send(row, csrfToken)
    if (outcome === "sent") sent++
    if (outcome === "failed") failed++
  }

  if (sent > 0 || failed > 0) _announceChange()
  return { sent, failed }
}

async function _send(storedRow, csrfToken) {
  // Read the photo BEFORE any write to the row: rewriting a row that still
  // holds an iOS Blob is what makes it unreadable.
  const prepared = await _preparedForSend(storedRow)
  const files = prepared.files
  const hasPhoto = files.length > 0
  let response

  const attempts = (prepared.row.attempts || 0) + 1
  await db.pending_cards.update(prepared.row.id, { attempts }).catch(() => {})
  const row = { ...prepared.row, attempts }

  try {
    response = await observedFetch(CARD_URL, {
      method: "POST",
      headers: {
        "Accept": "application/json",
        "X-CSRF-Token": csrfToken || ""
      },
      body: _formData(row, files),
      credentials: "same-origin",
      signal: AbortSignal.timeout(timeoutFor(hasPhoto ? "image_upload" : "json_write_light", currentLevel()))
    }, { kind: "card", sizeClass: hasPhoto ? "large" : "small" })
  } catch (error) {
    logWarn("cards", "Card send deferred -- network", {
      clientId: row.clientId,
      error: error?.name || String(error)
    }).catch(() => {})
    return "retry"
  }

  const outcome = response.status === 419 ? "reauth-required" : await classifySyncResponse(response)

  if (outcome === "success") {
    const payload = await response.json().catch(() => ({}))
    await db.pending_cards.update(row.id, {
      status: "sent",
      sentAt: new Date().toISOString(),
      cardId: payload.card_id || null,
      cardName: payload.name || null,
      photo: null,
      photoBytes: null,
      photos: [],
      error: null
    })
    logEvent("cards", "Card sent", {
      clientId: row.clientId,
      cardId: payload.card_id,
      replayed: Boolean(payload.replayed)
    }).catch(() => {})
    if (row.photoDropped) _logSentWithoutPhoto(row)
    return "sent"
  }

  if (outcome === "reauth-required") {
    await db.pending_cards.update(row.id, { status: "reauth-required" })
    logWarn("cards", "Card send paused -- sign in again", {
      clientId: row.clientId,
      status: response.status
    }).catch(() => {})
    _announceChange()
    return "reauth-required"
  }

  if (outcome === "failed") {
    const payload = await response.json().catch(() => ({}))
    if (isCutOffRejection(row, payload.errors)) return _handleCutOff(row, response.status, csrfToken)

    const message = (payload.errors || []).join(", ") || `Rejected (${response.status})`
    await db.pending_cards.update(row.id, { status: "failed", error: message })
    logError("cards", "Card failed", {
      clientId: row.clientId,
      status: response.status,
      error: message
    }).catch(() => {})
    return "failed"
  }

  logWarn("cards", "Card send deferred -- server", {
    clientId: row.clientId,
    status: response.status
  }).catch(() => {})
  return "retry"
}

// Identifiers and text BEFORE the file: multipart fields arrive in append
// order, so a photo upload cut off mid-stream still reaches the server with
// its client_id intact (the #2864 FormData-order lesson).
function _formData(row, files) {
  const context = row.context || {}
  const form = new FormData()

  form.append("client_id", row.clientId)
  // Rows queued before the Hub select existed have no hubId; the server files
  // those to the first allowlisted Hub (General IT).
  _appendIfPresent(form, "hub_id", row.hubId)
  for (const name of row.categoryTags || []) form.append("category_tags[]", name)
  // Rows queued before category tags carry a single slug ("sync"); the server
  // maps it to the Hub's "<prefix>sync" tag when one exists.
  _appendIfPresent(form, "category", row.category)
  form.append("queued_at", row.queuedAt)
  _appendIfPresent(form, "place_id", context.placeId)
  _appendIfPresent(form, "mission_id", context.missionId)
  _appendIfPresent(form, "response_id", context.responseId)
  _appendIfPresent(form, "platform_os", context.platformOs)
  _appendIfPresent(form, "display_mode", context.displayMode)
  _appendIfPresent(form, "app_version", context.appVersion)
  form.append("body", row.body || "")

  for (const file of files) form.append("photos[]", file, file.name)

  return form
}

// -- photo bytes --

// What goes on the queued row for the chosen photos: plain bytes plus the type
// and name to rebuild each with. A photo that is empty or will not read is not
// queued, and the row records how many were lost (photosDropped) so the Tasker
// is told.
async function _photoFields(blobs) {
  const list = blobs.filter(blob => blob instanceof Blob)
  if (list.length === 0) return { fields: {}, unreadable: false }

  const photos = []
  for (const blob of list) {
    const bytes = await _readBytes(blob)
    if (bytes) photos.push({ bytes, type: blob.type || "image/jpeg", name: blob.name || null })
  }

  const lost = list.length - photos.length
  const fields = { photos, photoTotal: list.length }
  if (lost > 0) Object.assign(fields, { photoDropped: PHOTO_DROPPED.UNREADABLE, photosDropped: lost })

  return { fields, unreadable: lost > 0 }
}

// The photo's bytes, or null when it is empty or cannot be read.
async function _readBytes(blob) {
  try {
    if (!blob || blob.size === 0) return null
    const bytes = await blob.arrayBuffer()
    return bytes.byteLength > 0 ? bytes : null
  } catch {
    return null
  }
}

// A fresh File built from a stored photo, or null when its bytes are empty or
// the wrong shape.
function _fileFromPhoto(photo, row, index) {
  if (!(photo?.bytes instanceof ArrayBuffer) || photo.bytes.byteLength === 0) return null
  return new File([photo.bytes], photo.name || `card-${row.clientId}-${index + 1}.jpg`, { type: photo.type || "image/jpeg" })
}

// Returns the row as it should be sent and the Files to send with it. Photos
// that cannot be read go without them, and the row says so.
async function _preparedForSend(storedRow) {
  let row = storedRow
  if (row.photo instanceof Blob) row = await _migrateLegacyPhoto(row)
  if (row.photoBytes !== undefined && row.photoBytes !== null) row = await _migrateSinglePhotoBytes(row)

  const photos = row.photos || []
  const files = []
  const readable = []

  photos.forEach((photo, index) => {
    const file = _fileFromPhoto(photo, row, index)
    if (!file) return
    files.push(file)
    readable.push(photo)
  })

  const lost = photos.length - readable.length
  if (lost > 0) row = await _dropPhotos(row, PHOTO_DROPPED.UNREADABLE, lost, readable)

  return { row, files }
}

// Rows queued before card #4115 hold a Blob. Read it once: if it gives up real
// bytes they move onto the row as a one-photo list, if not the photo is dropped.
async function _migrateLegacyPhoto(row) {
  const bytes = await _readBytes(row.photo)
  if (!bytes) return _dropPhotos({ ...row, photo: null }, PHOTO_DROPPED.UNREADABLE, 1, [])

  const fields = {
    photo: null,
    photos: [{ bytes, type: row.photo.type || "image/jpeg", name: row.photo.name || null }],
    photoTotal: row.photoTotal || 1
  }
  await db.pending_cards.update(row.id, fields).catch(() => {})
  return { ...row, ...fields }
}

// Rows queued by card #4115 hold one photo as photoBytes / photoType /
// photoName. Move it onto the row as a one-photo list. Empty or wrong-shaped
// bytes are dropped, same as any unreadable photo.
async function _migrateSinglePhotoBytes(row) {
  const usable = row.photoBytes instanceof ArrayBuffer && row.photoBytes.byteLength > 0
  const fields = { photoBytes: null, photoType: null, photoName: null }

  if (!usable) return _dropPhotos({ ...row, ...fields }, PHOTO_DROPPED.UNREADABLE, 1, [])

  Object.assign(fields, {
    photos: [{ bytes: row.photoBytes, type: row.photoType || "image/jpeg", name: row.photoName || null }],
    photoTotal: row.photoTotal || 1
  })
  await db.pending_cards.update(row.id, fields).catch(() => {})
  return { ...row, ...fields }
}

// Takes `count` photos off the row, keeping the `keep` ones that are still
// good, and records why so the Tasker is told which did not go.
async function _dropPhotos(row, reason, count, keep) {
  const alreadyDropped = row.photosDropped || 0
  const fields = {
    photo: null,
    photoBytes: null,
    photoType: null,
    photoName: null,
    photos: keep,
    photoTotal: row.photoTotal || keep.length + alreadyDropped + count,
    photoDropped: reason,
    photosDropped: alreadyDropped + count
  }
  await db.pending_cards.update(row.id, fields).catch(() => {})
  const dropped = { ...row, ...fields }

  if (reason === PHOTO_DROPPED.UNREADABLE) _logPhotoUnreadable(dropped, "send")
  _announceChange()
  return dropped
}

// Request arrived cut off. Patient for a few goes, then stop sending the photo
// that is probably the cause; a text-only row that keeps coming back cut off
// is shown as not sent rather than retried silently for ever.
async function _handleCutOff(row, status, csrfToken) {
  const cutOffCount = (row.cutOffCount || 0) + 1
  await db.pending_cards.update(row.id, { cutOffCount }).catch(() => {})

  logWarn("cards", "Card send deferred -- request arrived cut off", {
    clientId: row.clientId,
    status,
    cutOffCount,
    hasPhoto: _hasPhotos(row)
  }).catch(() => {})

  if (_hasPhotos(row) && cutOffCount >= CUT_OFF_PHOTO_LIMIT) {
    const dropped = await _dropPhotos({ ...row, cutOffCount: 0 }, PHOTO_DROPPED.CUT_OFF, row.photos.length, [])
    await db.pending_cards.update(row.id, { cutOffCount: 0 }).catch(() => {})
    return _send(dropped, csrfToken)
  }

  if (!_hasPhotos(row) && cutOffCount >= CUT_OFF_TEXT_LIMIT) {
    const message = "This ticket keeps getting cut off on its way to us. Remove it and try again, or use Send by text message."
    await db.pending_cards.update(row.id, { status: "failed", error: message })
    logError("cards", "Card failed", { clientId: row.clientId, status, error: message, cutOffCount }).catch(() => {})
    return "failed"
  }

  return "retry"
}

function _hasPhotos(row) {
  return (row.photos || []).length > 0
}

function _logPhotoUnreadable(row, stage) {
  logWarn("cards", "Card photo unreadable", {
    clientId: row.clientId,
    photosDropped: row.photosDropped || 0,
    photoTotal: row.photoTotal || 0,
    attempts: row.attempts || 0,
    platform: getPlatformSnapshot().os,
    stage
  }).catch(() => {})
}

function _logSentWithoutPhoto(row) {
  logWarn("cards", "Card sent without photo", {
    clientId: row.clientId,
    attempts: row.attempts || 0,
    platform: getPlatformSnapshot().os,
    photosDropped: row.photosDropped || 0,
    photoTotal: row.photoTotal || 0,
    reason: row.photoDropped
  }).catch(() => {})
}

function _appendIfPresent(form, key, value) {
  if (value === null || value === undefined || value === "") return
  form.append(key, String(value))
}

async function _purgeOldSent() {
  const cutoff = new Date(Date.now() - SENT_TTL_MS).toISOString()
  const stale = await db.pending_cards
    .where("status").equals("sent")
    .filter(row => row.sentAt && row.sentAt < cutoff)
    .primaryKeys()
    .catch(() => [])
  if (stale.length > 0) await db.pending_cards.bulkDelete(stale).catch(() => {})
}

function _announceChange() {
  if (typeof window === "undefined") return
  window.dispatchEvent(new CustomEvent(CARDS_CHANGED_EVENT))
}

function _uuid() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID()

  // crypto.randomUUID needs a secure context and iOS 15.4+; fall back to the
  // same v4 layout built from getRandomValues.
  const bytes = crypto.getRandomValues(new Uint8Array(16))
  bytes[6] = (bytes[6] & 0x0f) | 0x40
  bytes[8] = (bytes[8] & 0x3f) | 0x80
  const hex = Array.from(bytes, b => b.toString(16).padStart(2, "0")).join("")
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`
}
